import styles from './GrantsPage.module.scss'
import { Badge, Box, Button, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { useAuthState, useCausesPortalAnalytics, useIntroModalState } from '@percent/cause-dashboard/common/hooks'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { createChatTargets } from '@percent/cause-dashboard/services/aichat/aichat'

type GrantsPageProps = {
  pagesVisible: boolean
  page: number
  pagesCount: number
  height: number
  width: number
}

export function GrantsPage({ pagesVisible, page, pagesCount, height, width }: Readonly<GrantsPageProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { authState } = useAuthState()
  const { track } = useCausesPortalAnalytics()
  const trackedOpen = useRef(false)
  const { recordGrantsAction } = useIntroModalState()

  const shouldShowPageIndicator = !!(page && pagesCount && pagesCount > 1)

  const handleCtaClick = () => {
    track('Grant Pro Modal Clicked', {
      organisation: authState.claimOrganisation?.displayName,
      organisationId: authState.claimOrganisation?.id,
      accountId: authState.user?.id,
      title: t('offersModal.grantsPage.title'),
      ctaText: t('offersModal.grantsPage.cta.primary'),
      shownOnHomepageModalPageNumber: page
    })
    recordGrantsAction('clicked')
    navigate(`${RoutePath.AI_GATEWAY}?target=${createChatTargets.goodstackGrants}`)
  }

  useEffect(() => {
    if (!trackedOpen.current) {
      track('Grant Pro Modal Shown', {
        organisation: authState.claimOrganisation?.displayName,
        organisationId: authState.claimOrganisation?.id,
        accountId: authState.user?.id,
        shownOnHomepageModalPageNumber: page
      })
      trackedOpen.current = true
    }
  }, [track, authState.claimOrganisation?.displayName, authState.claimOrganisation?.id, authState.user?.id, page])

  return (
    <div className={styles.modalCtn} style={{ height, width }}>
      <div className={`${styles.column} ${styles.leftColumn}`}>
        <div className={`${styles.leftCtn} ${!pagesVisible ? styles.lessPadding : ''}`}>
          {shouldShowPageIndicator && (
            <p className={styles.pageIndicator}>
              {page} {t('offersModal.page.of')} {pagesCount}
            </p>
          )}
          <Typography
            className={styles.title}
            style={{ marginTop: shouldShowPageIndicator ? '-16px' : '0' }}
            variant="h2"
          >
            <span role="img" aria-label="sparkles">
              🪄
            </span>{' '}
            {t('offersModal.grantsPage.title')}
          </Typography>
          <Box className={styles.paragraphs}>
            <Box className={styles.paragraph}>
              <p className={styles.subtitle}>
                <span role="img" aria-label="magnifying glass">
                  🔍
                </span>{' '}
                {t('offersModal.grantsPage.paragraph1.title')}
              </p>
              <ul className={styles.list}>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph1.bullet1')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph1.bullet2')}
                </li>
              </ul>
            </Box>
            <Box className={styles.paragraph}>
              <p className={styles.subtitle}>
                <span role="img" aria-label="writing hand">
                  ✍️
                </span>{' '}
                {t('offersModal.grantsPage.paragraph2.title')}
                <Badge className={styles.badge} variant="informative" size="small">
                  {t('offersModal.grantsPage.paragraph2.badge')}
                </Badge>
              </p>
              <ul className={styles.list}>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph2.bullet1')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph2.bullet2')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph2.bullet3')}
                </li>
              </ul>
            </Box>
            <Box className={styles.paragraph}>
              <p className={styles.subtitle}>
                <span role="img" aria-label="stopwatch">
                  ⏱️
                </span>{' '}
                {t('offersModal.grantsPage.paragraph3.title')}
              </p>
              <ul className={styles.list}>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph3.bullet1')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph3.bullet2')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph3.bullet3')}
                </li>
              </ul>
            </Box>
            <Box className={styles.paragraph}>
              <p className={styles.subtitle}>
                <span role="img" aria-label="money">
                  💸
                </span>{' '}
                {t('offersModal.grantsPage.paragraph4.title')}
              </p>
              <ul className={styles.list}>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph4.bullet1')}
                </li>
                <li className={`${styles.paragraph2} ${styles.bullet}`}>
                  {t('offersModal.grantsPage.paragraph4.bullet2')}
                </li>
              </ul>
            </Box>
          </Box>
        </div>
        <div className={styles.buttonWrapper}>
          <Button data-testid="grants-cta-button" variant="primary" onClick={handleCtaClick}>
            {t('offersModal.grantsPage.cta.primary')}
          </Button>
        </div>
      </div>
      <div className={`${styles.column} ${styles.rightColumn}`} />
    </div>
  )
}
